import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  MenuItem,
  Chip,
  Typography,
  Button,
  Tooltip,
} from "@mui/material";
import { NotificationManager } from "react-notifications";
import { GetInventoryReorderReport } from "../../../services/InventoryTrackerService";
import CommonLoader from "../../common/CommonLoader";
import useSortedPaging from "./useSortedPaging";

// Most urgent first; also the order of the status filter.
export const REORDER_STATUSES = [
  "Order Now",
  "Order Next Week",
  "Order in 2 Weeks",
  "Order in 3 Weeks",
  "OK",
  "Not Set",
];

const STATUS_COLORS = {
  "Order Now": "error",
  "Order Next Week": "warning",
  "Order in 2 Weeks": "info",
  "Order in 3 Weeks": "info",
  OK: "success",
  "Not Set": "default",
};

export const ReorderStatusChip = ({ status }) =>
  status ? (
    <Chip
      label={status}
      size="small"
      color={STATUS_COLORS[status] || "default"}
      variant={status === "Order Now" ? "filled" : "outlined"}
    />
  ) : null;

/**
 * ENB-584 reorder report: what to order now, next week, in 2 and in 3 weeks, from each product's
 * burn rate, lead time and stock on hand. Reorder point = lead time x burn rate + 10%.
 */
const ReorderReport = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("due");
  const [searchTerm, setSearchTerm] = useState("");

  const load = async () => {
    try {
      setLoading(true);
      const response = await GetInventoryReorderReport();
      setRows(response?.results || []);
    } catch {
      NotificationManager.error("Error loading the reorder report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visible = rows
    .filter((row) =>
      statusFilter === "due"
        ? REORDER_STATUSES.indexOf(row.reorderStatus) <= 3
        : !statusFilter || row.reorderStatus === statusFilter
    )
    .filter((row) =>
      [row.productName, row.itemNumber, row.vendorName].join(" ").toLowerCase().includes(searchTerm.toLowerCase())
    )
    // Most urgent first until a header is clicked.
    .sort(
      (a, b) =>
        REORDER_STATUSES.indexOf(a.reorderStatus) - REORDER_STATUSES.indexOf(b.reorderStatus) ||
        (a.weeksUntilReorder ?? 0) - (b.weeksUntilReorder ?? 0)
    );

  const columns = [
    { label: "Status", field: "reorderStatus", rank: (status) => REORDER_STATUSES.indexOf(status) },
    { label: "Product", field: "productName" },
    { label: "Item #", field: "itemNumber" },
    { label: "Vendor", field: "vendorName" },
    { label: "On Hand", field: "stockOnHand" },
    { label: "Reorder Point", field: "reorderPoint" },
    { label: "Weeks Left", field: "weeksUntilReorder" },
    { label: "Burn Rate Used", field: "burnRate" },
    { label: "Manual", field: "manualBurnRate" },
    { label: "60 Days", field: "burnRate60Days" },
    { label: "120 Days", field: "burnRate120Days" },
    { label: "Lead Time (days)", field: "leadTimeDays" },
  ];

  const { header, pageRows, pagination } = useSortedPaging(visible, columns);

  return (
    <div className="vlp-component-container">
      <span className="title">Reorder Report </span>
      <hr />
      {/* Same order as the other search rows: search text, filters, Search. */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          load();
        }}
        style={{ display: "flex", marginBottom: "16px", alignItems: "center", gap: "12px" }}
      >
        <Tooltip title="Search by product, item number or vendor" arrow placement="top">
          <TextField
            label="Search product, item #, vendor"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: "20%" }}
          />
        </Tooltip>
        <Tooltip title="Filter by reorder status" arrow placement="top">
          <TextField
            select
            label="Status"
            variant="outlined"
            size="small"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            InputLabelProps={{ shrink: true }}
            SelectProps={{ displayEmpty: true }}
            style={{ width: "15%" }}
          >
            <MenuItem value="due">Due in the next 3 weeks</MenuItem>
            <MenuItem value="">All</MenuItem>
            {REORDER_STATUSES.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </TextField>
        </Tooltip>
        <Button variant="contained" color="primary" type="submit">
          Search
        </Button>
        <Typography variant="body2" color="text.secondary">
          Reorder point = lead time x burn rate + 10%. Burn rates are units per week.
        </Typography>
      </form>

      <Paper style={{ marginRight: "20px" }}>
        <TableContainer>
          <Table sx={{ minWidth: 400 }} size="small">
            <TableHead sx={{ backgroundColor: (theme) => theme.palette.primary.main }}>
              {header}
            </TableHead>
            <TableBody>
              {pageRows.map((row, index) => (
                <TableRow
                  key={row.productId}
                  sx={{ backgroundColor: index % 2 === 0 ? "rgba(0, 0, 0, 0.04)" : "white" }}
                >
                  <TableCell>
                    <ReorderStatusChip status={row.reorderStatus} />
                  </TableCell>
                  <TableCell>{row.productName}</TableCell>
                  <TableCell>{row.itemNumber}</TableCell>
                  <TableCell>{row.vendorName}</TableCell>
                  <TableCell>{row.stockOnHand}</TableCell>
                  <TableCell>{row.reorderStatus === "Not Set" ? "" : row.reorderPoint}</TableCell>
                  <TableCell>{row.weeksUntilReorder ?? ""}</TableCell>
                  <TableCell>
                    {row.burnRate} {row.useCalculatedBurnRate ? "(60 days)" : "(manual)"}
                  </TableCell>
                  <TableCell>{row.manualBurnRate ?? ""}</TableCell>
                  <TableCell>{row.burnRate60Days}</TableCell>
                  <TableCell>{row.burnRate120Days}</TableCell>
                  <TableCell>{row.leadTimeDays ?? ""}</TableCell>
                </TableRow>
              ))}
              {!loading && visible.length === 0 && (
                <TableRow>
                  <TableCell colSpan={12}>Nothing to reorder.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {pagination}
      </Paper>
      <CommonLoader loading={loading} />
    </div>
  );
};

export default ReorderReport;
